/**
 * PGSS-5382 / PGSS-5347 — the VAS filter choices, built once.
 *
 * The order form offers Sandblasting and Etching (`VasConfig.initVasData.services`).
 * Plotting is a production phase, not a type stored on the order, so it is not here.
 *
 * Selection model, used by the Sales dashboard and the Orders list (PGSS-5347):
 *   { mode: 'all' | 'non_vas' | 'types', types: string[] }
 *
 * VAS is order-level, so an order is either VAS or not. The Orders list is
 * single-select: All types (every VAS order) or one form type. Picking both
 * types would only hide image-only VAS, which All types already covers.
 */

export const VAS_FILTER_ALL = 'all';
export const VAS_FILTER_NON_VAS = 'non_vas';

/**
 * PGSS-5347: four selections, because "has" and "only" answer different questions and the gap
 * between them is real — ~206 production orders carry both kinds of work.
 *
 * - **Has** — everything that must pass through that station, mixed orders included. This is the
 *   station-planning question the ticket was raised for.
 * - **Only** — orders that station can finish without a hand-off.
 *
 * Both words appear, so they define each other and neither has to be inferred. "Has etching" is
 * 338 production orders against "Only etching" at 132 — labelling the inclusive one "only" would
 * have been wrong on the majority of what it returned.
 *
 * Shared with the Sales Dashboard so the two screens cannot drift apart.
 */
const STATION_CHOICES = [
  { value: 'sandblasting', label: 'Has sandblasting' },
  { value: 'etching', label: 'Has etching' },
  { value: 'sandblasting_only', label: 'Only sandblasting' },
  { value: 'etching_only', label: 'Only etching' },
];

/**
 * PGSS-5417: the client asked for four buckets and only two of them were reachable — "Only
 * sandblasting" and "Only etching". These are the other two.
 *
 * - **Sandblasting and etching** — the work needing BOTH stations, so it needs a hand-off between
 *   them. Not derivable from the four above by any single click: "Has sandblasting" contains it but
 *   also contains sandblasting-only, and the set is `has minus only`, which is arithmetic rather
 *   than a filter. Roughly 206 production orders sit in it.
 * - **Has cameo (porcelain)** — the photo plaque mounted on the stone. Not a station: it is an
 *   *include*, an item that goes onto the stone, so it asks a different question from the other
 *   three — do we have to buy a plaque, machine a recess for it and fit it.
 *
 * Added as two more single-select entries rather than by making the control multi-select. Every
 * bucket is then one click, and the selection model, the URL parameter and the dashboard are all
 * untouched.
 */
const BOTH_STATIONS_CHOICE = { value: 'sandblasting_and_etching', label: 'Sandblasting and etching' };
const PORCELAIN_CHOICE = { value: 'porcelain', label: 'Has cameo (porcelain)' };

/** Orders list. Six choices: the four station ones plus both of the above. */
export const VAS_TYPE_CHOICES = [...STATION_CHOICES, BOTH_STATIONS_CHOICE, PORCELAIN_CHOICE];

/**
 * Sales Dashboard: the four station choices plus both-stations — but NOT the cameo.
 *
 * Both-stations is answerable here: an order billed for sandblasting AND billed for etching.
 *
 * A cameo is not. The dashboard classifies by what was BILLED, and there is no porcelain charge
 * type — `VasAmountsResponse` carries exactly two money buckets, sandblasting and etching, so a
 * cameo has no revenue representation at all and at best lands in OTHERS, which cannot distinguish
 * it. Answering it from the design JSON instead would put two different questions in one dropdown,
 * which is the PGSS-5383 defect: orders matched no option at all and their revenue left every
 * total with them. Giving cameos their own charge type is the honest fix, and it is a data change,
 * not a filter change.
 *
 * ⚠️ Both-stations will NOT return the same count as the Orders list's identically-labelled choice.
 * This screen follows the money; that one follows the design; the two records genuinely disagree.
 * Expect the question, and answer it with "billed for both" versus "planned as both".
 */
export const DASHBOARD_VAS_TYPE_CHOICES = [...STATION_CHOICES, BOTH_STATIONS_CHOICE];

export const EMPTY_VAS_FILTER = Object.freeze({ mode: 'all', types: [] });

export function getVasFilterChoices() {
  return [
    { value: VAS_FILTER_ALL, label: 'All orders' },
    { value: VAS_FILTER_NON_VAS, label: 'Non-VAS' },
    ...DASHBOARD_VAS_TYPE_CHOICES,
  ];
}

export function isVasType(value) {
  return VAS_TYPE_CHOICES.some((choice) => choice.value === value);
}

/**
 * Apply a single choice click. All / Non-VAS replace the selection. A type
 * becomes the only selected type (dashboard toolbar is single-select). The
 * Orders list can still send `{ mode: 'types', types: [...] }` directly.
 */
export function vasFilterFromChoice(value) {
  if (value === VAS_FILTER_NON_VAS) {
    return { mode: 'non_vas', types: [] };
  }
  if (isVasType(value)) {
    return { mode: 'types', types: [value] };
  }
  return { mode: 'all', types: [] };
}

export function vasFilterToRequest(filter) {
  if (!filter || filter.mode === 'all') {
    return undefined;
  }
  if (filter.mode === 'non_vas') {
    return { mode: 'non_vas' };
  }
  return { mode: 'types', types: [...(filter.types || [])] };
}

export function vasFilterSelectValue(filter) {
  if (!filter || filter.mode === 'all') {
    return VAS_FILTER_ALL;
  }
  if (filter.mode === 'non_vas') {
    return VAS_FILTER_NON_VAS;
  }
  return filter.types?.[0] || VAS_FILTER_ALL;
}

/** Orders list URL / API: `vas_types=sandblasting`. Unknown values are dropped; only the first type is kept. Writer and reader are the same cap. */
export function parseVasTypesParam(value) {
  if (!value || typeof value !== 'string') {
    return [];
  }
  const types = [...new Set(value.split(',').map((part) => part.trim()).filter(isVasType))];
  return types.slice(0, 1);
}

export function formatVasTypesParam(types) {
  const selected = new Set(types || []);
  const first = VAS_TYPE_CHOICES.map((choice) => choice.value).find((type) => selected.has(type));
  return first;
}

/** Types are a sub-selection of Only VAS, and only on the Orders list. */
export function shouldShowOrderVasTypes(showOnlyVAS, listType) {
  return Boolean(showOnlyVAS) && listType === 'order';
}

export function vasTypeFilterLabels(types) {
  const selected = VAS_TYPE_CHOICES.filter((choice) => (types || []).includes(choice.value));
  if (!selected.length) {
    return 'All types';
  }
  return selected.map((choice) => choice.label).join(', ');
}
